import { translate } from '../../../utils/translations';

import { extractPhoneDigits, LOGIN_LIMITS, validateEmail, validatePhone } from './validators';

export type IdentifierType = 'email' | 'phone' | 'unknown';

export interface ParsedIdentifier {
    type: IdentifierType;
    /** Raw email text (no whitespace, capped at the email limit); empty unless type is "email". */
    email: string;
    /** National 10-digit number; empty unless type is "phone". */
    phone: string;
}

const EMAIL_HINT = /[A-Za-z@]/;
const PHONE_FORMATTING = /[()\s.-]/g;

/**
 * Live-typing detection: any letter or "@" means email; otherwise digits mean phone.
 * Empty input (or only punctuation such as "(" or "+") is "unknown".
 */
export const detectIdentifierType = (text: string): IdentifierType => {
    if (EMAIL_HINT.test(text)) return 'email';
    if (/\d/.test(text)) return 'phone';
    return 'unknown';
};

/**
 * Turns what the user typed/pasted into an email or a phone number.
 * `previousType` matters when the user types a letter right after digits: the visible text is the
 * formatted phone ("(123"), so the phone formatting is stripped to recover the digits typed so far.
 */
export const parseIdentifierInput = (text: string, previousType: IdentifierType): ParsedIdentifier => {
    const type = detectIdentifierType(text);
    if (type === 'email') {
        const base = previousType === 'phone' ? text.replace(PHONE_FORMATTING, '') : text;
        return { type, email: base.replace(/\s/g, '').slice(0, LOGIN_LIMITS.EMAIL_MAX_LENGTH), phone: '' };
    }
    if (type === 'phone') {
        return { type, email: '', phone: extractPhoneDigits(text) };
    }
    return { type: 'unknown', email: '', phone: '' };
};

/** Validates the single identifier field; returns an error message or null. */
export const validateIdentifier = (type: IdentifierType, email: string, phone: string): string | null => {
    if (type === 'email') return validateEmail(email);
    if (type === 'phone') return validatePhone(phone);
    return translate('authFlow.validation.identifierRequired');
};
